import React from 'react';
import { useWeddingContext } from '../context/WeddingContext';

interface HeaderProps {
  user: any;
  signOut: () => void;
}

const Header: React.FC<HeaderProps> = ({ user, signOut }) => {
  const { settings, weddingGroup } = useWeddingContext();

  const handleLogout = async () => {
      try {
        await signOut();
      } catch (error) {
      console.error('ログアウトに失敗しました:', error);
    }
  };

  const calculateDaysUntilWedding = (): string => {
    if (!settings.weddingDate) {
      return '挙式日を設定してください';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const weddingDate = new Date(settings.weddingDate);
    weddingDate.setHours(0, 0, 0, 0);
    
    const timeDiff = weddingDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    if (daysDiff > 0) {
      return `あと${daysDiff}日`;
    } else if (daysDiff === 0) {
      return '本日です！';
    } else {
      return '終了しました';
    }
  };

  const formatWeddingDate = (): string => {
    if (!settings.weddingDate) return '';
    const date = new Date(settings.weddingDate);
    return `挙式予定日: ${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const getUserDisplayInfo = () => {
    // 実際のメールアドレスを取得（複数の可能性をチェック）
    const email = user?.signInDetails?.loginId || 
                  user?.attributes?.email || 
                  user?.email || 
                  user?.username || 
                  'unknown@example.com';
    
    // displayNameを改善：メールアドレスの@より前部分を使用
    let displayName = email.includes('@') ? email.split('@')[0] : email;
    
    // ファミリーグループのメンバー情報から表示名を取得
    if (weddingGroup?.members && user?.username) {
      const currentUserMember = weddingGroup.members.find(member => 
        member.userId === user.username || member.email === email
      );
      if (currentUserMember?.displayName) {
        displayName = currentUserMember.displayName;
      }
    }
    
    // ファミリー情報の取得
    const familyName = weddingGroup?.familyName || '';
    const isInFamily = !!familyName && !!weddingGroup?.members && weddingGroup.members.length > 1;
    
    return {
      email,
      displayName,
      isInFamily,
      familyName: familyName || displayName || 'アカウント'
    };
  };

  const userInfo = getUserDisplayInfo();

  return (
    <header style={{ marginBottom: 'var(--spacing-6)', position: 'relative' }}>
      {/* ログアウトボタン - 右上固定 */}
        <button
          onClick={handleLogout}
        className="logout-btn"
        style={{ 
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px',
          padding: '6px 12px',
          fontSize: '13px',
          minHeight: '32px',
          width: 'auto',
          background: 'linear-gradient(135deg, #f3e8ff, #e9d5ff)',
          color: '#7c3aed',
          border: '1px solid #d8b4fe',
          borderRadius: '6px',
          fontWeight: '500',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.1)'
        }}
      >
        <span style={{ fontSize: '13px' }}>🚪</span>
        <span>ログアウト</span>
        </button>

      {/* ユーザー情報バー - ログアウトボタン除外 */}
      <div className="card" style={{ 
        padding: 'var(--spacing-3) var(--spacing-4)', 
        marginBottom: 'var(--spacing-4)',
        background: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-3)',
        paddingRight: '120px' // ログアウトボタンのスペース確保
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          background: 'linear-gradient(135deg, var(--color-primary-400), var(--color-primary-600))',
          borderRadius: 'var(--radius-full)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--font-size-lg)',
          color: 'white'
        }}>
          {userInfo.isInFamily ? '👫' : '👤'}
        </div>
        <div style={{ flex: 1 }}>
          <div className="heading-6" style={{ marginTop: 0, marginLeft: 0, marginRight: 0, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span>{userInfo.familyName}</span>
            {userInfo.isInFamily && (
              <span className="badge badge-primary">Family</span>
            )}
          </div>
          <div className="body-small" style={{ color: 'var(--color-neutral-600)' }}>
            {userInfo.displayName} ({userInfo.email})
          </div>
        </div>
      </div>

      {/* Wedding Task Planner カウントダウン表示 */}
      <div className="text-center mb-8 relative">
        {/* 外側のオーラ効果 */}
        <div className="absolute -inset-2 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl blur-lg opacity-30"></div>
        
        {/* メインコンテナ - レスポンシブパディング */}
        <div className="relative p-4 sm:p-8 bg-white rounded-xl shadow-2xl border border-pink-200">
          <h1 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent mb-3 sm:mb-4">
            Wedding Task Planner
          </h1>
          
          <div className="p-3 sm:p-4 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg mb-3 sm:mb-4">
            <div className="text-base sm:text-xl font-semibold text-gray-700 mb-1">結婚式まで</div>
            <div className="text-3xl sm:text-5xl font-extrabold text-pink-500">
              {(() => {
                const daysText = calculateDaysUntilWedding();
                if (daysText.includes('あと') && daysText.includes('日')) {
                  const match = daysText.match(/あと(\d+)日/);
                  return match ? `あと${match[1]}日` : daysText;
                } else if (daysText.includes('本日')) {
                  return <span className="text-xl sm:text-3xl">本日です！</span>;
                } else if (daysText.includes('終了')) {
                  return <span className="text-xl sm:text-3xl text-gray-600">終了しました</span>;
                } else {
                  return <span className="text-lg sm:text-2xl">設定してください</span>;
                }
              })()}
            </div>
          </div>

          {settings.weddingDate && (
            <div className="text-sm sm:text-base text-pink-600 font-medium flex items-center justify-center space-x-2">
              <span>💐</span>
              <span>{formatWeddingDate()}</span>
              <span>💐</span>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header; 